/**
 * Karbon360° v2 — Overview.
 *
 * The landing screen: footprint summary, what is blocking an audit-ready
 * inventory, scope split, monthly trend, and site × month coverage.
 *
 * Every figure comes from a live endpoint. Where the API cannot yet supply
 * something the prototype showed, this page omits it rather than inventing a
 * number — on a product whose pitch is reproducibility, a decorative statistic
 * is worse than a missing one.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import ErrorBoundary from '../components/ErrorBoundary';
import { Shell } from '../components/Shell';
import { X } from '../components/v2/Icons';
import {
  PageHeader, Card, Button, Stat, Badge, Callout, Empty, Skeleton, fmt,
} from '../components/v2/ui';
import { Donut, MonthlyBars, Heatmap, SCOPE_COLORS } from '../components/v2/ui/Charts';
import { TaskRow } from '../components/v2/TaskRow';
import { buildActionInbox } from '../components/v2/action-inbox';
import { seedActionInbox } from '../components/v2/useActionInbox';
import { useT } from '../i18n';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5050';
const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });

/** How many tasks the card shows before deferring to the full page. */
const CARD_LIMIT = 4;

function Overview() {
  const navigate = useNavigate();
  const { t } = useT();
  const [dash, setDash] = useState(null);
  const [matrix, setMatrix] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const year = matrix?.year || new Date().getFullYear();

  useEffect(() => {
    let alive = true;
    const y = new Date().getFullYear();
    Promise.all([
      axios.get(`${API_URL}/api/dashboard?range=last-year`, auth()).then((r) => r.data),
      axios.get(`${API_URL}/api/emissions/coverage/matrix?year=${y}`, auth()).then((r) => r.data).catch(() => null),
    ])
      .then(([d, m]) => {
        if (!alive) return;
        setDash(d); setMatrix(m);
        seedActionInbox({ kpis: d?.kpis || null, matrix: m });
      })
      .catch((e) => { if (alive) setError(e.response?.data?.message || true); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, []);

  const kpis = dash?.kpis;
  const { tasks, counts } = useMemo(() => buildActionInbox({ kpis, matrix }), [kpis, matrix]);

  const scopeData = useMemo(() => {
    const s = kpis?.scopeSplit;
    if (!s) return [];
    return [
      { name: t('overview.scope1'), value: s.scope1 || 0, color: SCOPE_COLORS.scope1 },
      { name: t('overview.scope2'), value: s.scope2 || 0, color: SCOPE_COLORS.scope2 },
      { name: t('overview.scope3'), value: s.scope3 || 0, color: SCOPE_COLORS.scope3 },
    ];
  }, [kpis, t]);

  if (loading) {
    return (
      <div className="k2-wrap">
        <PageHeader title={t('overview.title')} desc={t('overview.loading')} />
        <div className="k2-g4" style={{ marginBottom: 24 }}>
          {[0, 1, 2, 3].map((i) => <Card key={i}><Skeleton h={54} /></Card>)}
        </div>
        <Card><Skeleton h={190} /></Card>
      </div>
    );
  }

  if (error) {
    return (
      <div className="k2-wrap">
        <PageHeader title={t('overview.title')} />
        <Callout tone="err">{typeof error === 'string' ? error : t('overview.loadError')}</Callout>
      </div>
    );
  }

  const total = kpis?.totalCurrentPeriod;
  const intensity = kpis?.intensity;
  const comp = kpis?.inventoryCompleteness;
  const vb = kpis?.vsBaseline;
  const hasAnyData = (dash?.breakdown || []).some((b) => Number(b.current) > 0) || Number(total?.value) > 0;

  return (
    <div className="k2-wrap">
      <PageHeader
        title="Overview"
        desc={t('overview.desc', {
          year: comp?.year ? ` ${comp.year}` : '',
          count: matrix?.sites?.length ?? 0,
          facilities: t((matrix?.sites?.length ?? 0) === 1 ? 'overview.facility' : 'overview.facilities')
        })}
      >
        <Button icon={X.Doc} onClick={() => navigate('/emissions')}>{t('overview.reports')}</Button>
        <Button variant="mint" icon={X.Plus} onClick={() => navigate('/regjistrimi')}>{t('overview.addData')}</Button>
      </PageHeader>

      {!hasAnyData && (
        <div style={{ marginBottom: 24 }}>
          <Callout tone="info">
            <b>{t('overview.noData')}</b> {t('overview.noDataBody')}
          </Callout>
        </div>
      )}

      {/* KPI strip */}
      <div className="k2-g4" style={{ marginBottom: 24 }}>
        <Stat
          tone="dark"
          label={t('overview.kpi.total', { period: total?.period || t('overview.kpi.currentPeriod') })}
          value={fmt(total?.value ?? 0, 2)}
          unit="tCO₂e"
          delta={total?.deltaPct}
          foot={total?.comparisonLabel}
        />
        <Stat
          label={t('overview.kpi.intensity')}
          value={fmt(intensity?.value ?? 0, 1)}
          unit="kgCO₂e/m²"
          delta={intensity?.deltaPct}
          foot={intensity?.totalAreaSqm ? t('overview.kpi.floorArea', { area: fmt(intensity.totalAreaSqm, 0) }) : t('overview.kpi.noFloorArea')}
        />
        <Stat
          label={t('overview.kpi.completeness')}
          value={comp ? `${comp.tracked}` : '—'}
          unit={comp ? `/ ${comp.outOf} ${t('common.months')}` : ''}
          foot={comp ? t('overview.kpi.completenessFoot', { complete: comp.complete, partial: comp.partial }) : ''}
        />
        <Stat
          tone={vb?.onTrack ? 'mint' : undefined}
          label={t('overview.kpi.vsBaseline', { year: vb?.baselineYear ?? t('overview.kpi.baseline') })}
          value={vb?.vsBaselinePct === null || vb?.vsBaselinePct === undefined ? '—' : `${fmt(vb.vsBaselinePct, 1)}`}
          unit={vb?.vsBaselinePct === null || vb?.vsBaselinePct === undefined ? '' : '%'}
          foot={
            vb?.vsBaselinePct === null || vb?.vsBaselinePct === undefined
              ? t('overview.kpi.noBaseline')
              : t(vb.onTrack ? 'overview.kpi.onTrack' : 'overview.kpi.offTrack', { year: vb.targetYear })
          }
        />
      </div>

      {/* Action inbox + scope split */}
      <div className="k2-g64" style={{ marginBottom: 24 }}>
        <Card
          title={t('inbox.cardTitle')}
          sub={t('inbox.cardSub')}
          right={counts.open > 0
            ? <Badge tone="warn" dot>{t('inbox.open', { count: counts.open })}</Badge>
            : <Badge tone="ok">{t('inbox.clear')}</Badge>}
          bodyProps={{ style: { padding: tasks.length ? '4px 0 0' : undefined } }}
          footer={
            <Button
              variant="gh"
              icon={X.Inbox}
              style={{ marginLeft: 'auto' }}
              onClick={() => navigate('/action-inbox')}
            >
              {tasks.length > CARD_LIMIT ? t('inbox.seeAll', { count: tasks.length }) : t('inbox.openInbox')}
              <X.Chev s={13} />
            </Button>
          }
        >
          {tasks.length === 0 ? (
            <Empty
              icon={X.Check}
              title={t('inbox.emptyTitle')}
              body={t('inbox.emptyBody')}
            />
          ) : (
            tasks.slice(0, CARD_LIMIT).map((t) => <TaskRow key={t.id} task={t} />)
          )}
        </Card>

        <Card
          title={t('overview.scopeSplit')}
          sub={t('overview.scopeSplitSub')}
          footer={
            <span style={{ fontSize: 12, color: 'var(--n500)', display: 'flex', alignItems: 'center', gap: 6 }}>
              <X.Info s={13} />
              Scope 3 covers business travel and commuting — 2 of 15 categories.
            </span>
          }
        >
          <Donut data={scopeData} />
        </Card>
      </div>

      {/* Monthly trend */}
      <Card
        title="Monthly emissions"
        sub="Last 6 months · the black tick marks the −10% year-on-year goal for that month"
        className="k2-mb-gut"
      >
        {(dash?.breakdown || []).length === 0
          ? <Empty icon={X.Chart} title="No monthly data yet" body="Record a month of activity to see the trend." />
          : <MonthlyBars data={dash.breakdown} />}
      </Card>

      {/* Coverage heatmap */}
      <div style={{ marginTop: 24 }}>
        <Card
          title="Data coverage"
          sub={`Every facility × month in ${year} — gaps block assurance`}
          right={
            matrix?.summary
              ? <Badge tone="out">{matrix.summary.coveragePct}% covered</Badge>
              : null
          }
        >
          <Heatmap
            sites={matrix?.sites || []}
            onCell={(site, m) => {
              if (m.state === 'none') navigate('/regjistrimi');
              else navigate('/invoices-new');
            }}
          />
          {matrix?.unassignedRecords > 0 && (
            <div style={{ marginTop: 14 }}>
              <Callout tone="warn">
                <b>{matrix.unassignedRecords} record{matrix.unassignedRecords > 1 ? 's are' : ' is'} not linked to a facility</b> and
                therefore do not appear in this grid.
              </Callout>
            </div>
          )}
        </Card>
      </div>
    </div>
  );
}

export default function OverviewPage() {
  return (
    <ErrorBoundary>
      <Shell>
        <Overview />
      </Shell>
    </ErrorBoundary>
  );
}
